import { useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Calculator, Coins, IdentificationBadge, Money, CurrencyInr, Bank } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TABS = [
  { key: "mortgage", label: "Mortgage", icon: Calculator },
  { key: "dld", label: "DLD & Fees", icon: Money },
  { key: "roi", label: "Rental Yield", icon: Coins },
  { key: "visa", label: "Golden Visa", icon: IdentificationBadge },
  { key: "inr", label: "AED vs INR", icon: CurrencyInr },
  { key: "prequal", label: "Pre-qualify", icon: Bank },
];

export default function Calculators() {
  const [tab, setTab] = useState("mortgage");
  return (
    <div data-testid="calculators-page">
      <SEO
        title="Dubai property calculators"
        description="Run the numbers on your Dubai purchase — mortgage, rental yield, DLD fees, Golden Visa eligibility, INR appreciation and pre-qualification."
        path="/calculators"
      />
      <section className="pt-6 pb-8 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Calculators", path: "/calculators" }]} />
          <div className="overline text-[#001588] mb-4">Tools</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-4xl">Do the math<br/><span className="italic text-neutral-600">before you fall in love.</span></h1>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2 mb-10 border-b border-neutral-200 pb-2">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key} onClick={() => setTab(key)}
                data-testid={`calc-tab-${key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm tracking-wide transition-colors ${
                  tab === key ? "bg-[#001588] text-white" : "border border-neutral-300 text-neutral-600 hover:border-[#001588] hover:text-[#001588]"
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          {tab === "mortgage" && <MortgageCalc />}
          {tab === "dld" && <DldCalc />}
          {tab === "roi" && <RoiCalc />}
          {tab === "visa" && <VisaCalc />}
          {tab === "inr" && <InrCalc />}
          {tab === "prequal" && <PrequalTeaser />}
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="overline text-neutral-500 block mb-2">{label}</span>
      <input {...rest} data-testid={testId} className="w-full bg-white border border-[#e5e7eb] px-4 py-3 text-[#050d19] focus:outline-none focus:border-[#001588]" />
    </label>
  );
}
function Result({ items, testId }) {
  return (
    <div className="border border-[#001588]/40 p-8 bg-[#ffffff]" data-testid={testId}>
      <div className="overline text-[#001588] mb-4">Result</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {items.map(([k, v]) => (
          <div key={k}>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{k}</div>
            <div className="font-serif text-2xl text-[#001588]">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MortgageCalc() {
  const [price, setPrice] = useState(2500000);
  const [dp, setDp] = useState(20);
  const [rate, setRate] = useState(4.5);
  const [years, setYears] = useState(25);
  const [res, setRes] = useState(null);
  async function run() {
    const { data } = await api.post("/calc/mortgage", { price, down_payment_percent: dp, interest_rate: rate, years });
    setRes(data);
  }
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Property price (AED)" type="number" value={price} onChange={(e) => setPrice(+e.target.value)} testId="calc-mortgage-price" />
        <Field label="Down payment (%)" type="number" value={dp} onChange={(e) => setDp(+e.target.value)} testId="calc-mortgage-dp" />
        <Field label="Interest rate (%)" type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} testId="calc-mortgage-rate" />
        <Field label="Term (years)" type="number" value={years} onChange={(e) => setYears(+e.target.value)} testId="calc-mortgage-years" />
        <button onClick={run} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-mortgage-btn">Calculate</button>
      </div>
      <div>{res && <Result testId="calc-mortgage-result" items={[
        ["Monthly payment", `AED ${res.monthly_payment.toLocaleString()}`],
        ["Down payment", `AED ${res.down_payment.toLocaleString()}`],
        ["Loan principal", `AED ${res.principal.toLocaleString()}`],
        ["Total interest", `AED ${res.total_interest.toLocaleString()}`],
      ]} />}</div>
    </div>
  );
}

function DldCalc() {
  const [price, setPrice] = useState(2500000);
  const [agency, setAgency] = useState(2);
  const [res, setRes] = useState(null);
  async function run() {
    const { data } = await api.post("/calc/dld", { price, agency_percent: agency });
    setRes(data);
  }
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Property price (AED)" type="number" value={price} onChange={(e) => setPrice(+e.target.value)} testId="calc-dld-price" />
        <Field label="Agency fee (%)" type="number" value={agency} onChange={(e) => setAgency(+e.target.value)} testId="calc-dld-agency" />
        <button onClick={run} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-dld-btn">Calculate</button>
      </div>
      <div>{res && <Result testId="calc-dld-result" items={[
        ["DLD transfer (4%)", `AED ${res.dld_transfer_fee.toLocaleString()}`],
        ["Admin fee", `AED ${res.admin_fee.toLocaleString()}`],
        ["Trustee fee", `AED ${res.trustee_fee.toLocaleString()}`],
        ["Agency fee", `AED ${res.agency_fee.toLocaleString()}`],
        ["Total upfront", `AED ${res.total_upfront_fees.toLocaleString()}`],
      ]} />}</div>
    </div>
  );
}

function RoiCalc() {
  const [price, setPrice] = useState(2500000);
  const [rent, setRent] = useState(180000);
  const [service, setService] = useState(20000);
  const [res, setRes] = useState(null);
  async function run() {
    const { data } = await api.post("/calc/roi", { price, annual_rent: rent, service_charges: service });
    setRes(data);
  }
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Property price (AED)" type="number" value={price} onChange={(e) => setPrice(+e.target.value)} testId="calc-roi-price" />
        <Field label="Annual rent (AED)" type="number" value={rent} onChange={(e) => setRent(+e.target.value)} testId="calc-roi-rent" />
        <Field label="Annual service charges (AED)" type="number" value={service} onChange={(e) => setService(+e.target.value)} testId="calc-roi-service" />
        <button onClick={run} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-roi-btn">Calculate</button>
      </div>
      <div>{res && <Result testId="calc-roi-result" items={[
        ["Gross yield", `${res.gross_yield_percent}%`],
        ["Net yield", `${res.net_yield_percent}%`],
        ["Net annual income", `AED ${res.net_annual_income.toLocaleString()}`],
      ]} />}</div>
    </div>
  );
}

function VisaCalc() {
  const [price, setPrice] = useState(2000000);
  const [res, setRes] = useState(null);
  async function run() {
    const { data } = await api.post("/calc/golden-visa", { property_value: price });
    setRes(data);
  }
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Property value (AED)" type="number" value={price} onChange={(e) => setPrice(+e.target.value)} testId="calc-visa-price" />
        <button onClick={run} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-visa-btn">Check eligibility</button>
      </div>
      <div>{res && (
        <div className="border border-[#001588]/40 p-8 bg-[#ffffff]" data-testid="calc-visa-result">
          <div className="overline text-[#001588] mb-4">Eligibility</div>
          <div className="space-y-4">
            <Row ok={res.eligible_10_year} label="10-year Golden Visa (≥ AED 2M)" />
            <Row ok={res.eligible_2_year} label="2-year investor visa (≥ AED 750K)" />
          </div>
          <p className="text-xs text-neutral-500 mt-6">{res.notes}</p>
        </div>
      )}</div>
    </div>
  );
}

function InrCalc() {
  const [aed_price, setAed] = useState(2500000);
  const [rate, setRate] = useState(22.60);
  const [years, setYears] = useState(5);
  const [app, setApp] = useState(2.8);
  const [res, setRes] = useState(null);
  async function run() {
    const { data } = await api.post("/calc/currency", { aed_price, aed_inr_rate: rate, years, annual_appreciation: app });
    setRes(data);
  }
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="Property price (AED)" type="number" value={aed_price} onChange={(e) => setAed(+e.target.value)} testId="calc-inr-aed" />
        <Field label="Current AED / INR rate" type="number" step="0.01" value={rate} onChange={(e) => setRate(+e.target.value)} testId="calc-inr-rate" />
        <Field label="Holding period (years)" type="number" value={years} onChange={(e) => setYears(+e.target.value)} testId="calc-inr-years" />
        <Field label="Expected annual appreciation (%)" type="number" step="0.1" value={app} onChange={(e) => setApp(+e.target.value)} testId="calc-inr-app" />
        <button onClick={run} className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-inr-btn">Compute currency gain</button>
        <p className="text-xs text-neutral-500">Assumes historical average AED / INR appreciation of 2.8% p.a. based on 2015–2025 RBI reference rates.</p>
      </div>
      <div>{res && <Result testId="calc-inr-result" items={[
        ["Value today (INR)", `₹ ${Number(res.inr_today).toLocaleString("en-IN")}`],
        [`Value in ${res.years}y (INR)`, `₹ ${Number(res.inr_future).toLocaleString("en-IN")}`],
        [`AED/INR rate in ${res.years}y`, res.aed_inr_future],
        ["Currency-only gain", `₹ ${Number(res.currency_gain_inr).toLocaleString("en-IN")}`],
      ]} />}</div>
    </div>
  );
}

function PrequalTeaser() {
  return (
    <div className="border border-neutral-200 p-10 bg-[#ffffff] text-center max-w-2xl mx-auto">
      <div className="overline text-[#001588] mb-3">Mortgage pre-qualification</div>
      <h3 className="font-serif text-3xl mb-4">Full form on a dedicated page.</h3>
      <p className="text-neutral-600 mb-6">Get an instant affordability estimate — indicative, then reviewed by our mortgage advisor within a business day.</p>
      <Link to="/mortgage-pre-qualification" className="inline-block px-8 py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors" data-testid="calc-prequal-cta">
        Open pre-qualification form
      </Link>
    </div>
  );
}

function Row({ ok, label }) {
  return (
    <div className={`flex items-center justify-between border-b border-neutral-200 pb-3 ${ok ? "" : "opacity-60"}`}>
      <span className="text-sm text-neutral-700">{label}</span>
      <span className={`text-sm font-medium ${ok ? "text-[#001588]" : "text-neutral-500"}`}>{ok ? "Eligible" : "Below threshold"}</span>
    </div>
  );
}
